/**
 * Loads Microsoft Clarity (session replay + heatmaps) on the client and, for
 * logged-in users, calls the Clarity Identify API so sessions are attributable
 * (Clarity hashes the custom id client-side; raw PII never leaves the browser).
 * Clarity is analytics only — not a conversion path.
 *
 * Complete no-op (no script injected) when `clarityProjectId` is empty.
 */
import { useUserStore } from '~/store/user';

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

export default defineNuxtPlugin(() => {
  const projectId = useRuntimeConfig().public.clarityProjectId as string;
  if (!projectId) return;

  // Official Clarity bootstrap (queue + async tag injection).
  window.clarity =
    window.clarity ||
    function clarity(...args: unknown[]) {
      (window.clarity!.q = window.clarity!.q || []).push(args);
    };
  const src = `https://www.clarity.ms/tag/${projectId}`;
  if (!document.querySelector(`script[src="${src}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }

  // Identify the logged-in buyer for session attribution. Reactive: fires when
  // the user logs in mid-session, and re-identifies on account switch.
  const userStore = useUserStore();
  const identify = (user: Record<string, any> | undefined) => {
    const client = user?.client as Record<string, any> | undefined;
    if (!client) return;
    const id = client.id ?? client.userid ?? client.email;
    if (!id) return;
    const friendlyName =
      [client.firstname, client.lastname].filter(Boolean).join(' ') ||
      undefined;
    window.clarity?.('identify', String(id), undefined, undefined, friendlyName);
  };

  identify(userStore.getUser as any);
  watch(
    () => userStore.getUser,
    (user) => identify(user as any),
  );
});
